import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectUser, injectOrg } from '@quipex/core/auth';
import { APP_CONFIG_TOKEN, type AppConfig } from '@quipex/core/config';
import { filter } from 'rxjs';
import { DATADOG_RUM_LOADER } from './loader';
import type {
    DatadogEnvironment,
    DatadogPrivacyLevel,
    DatadogRumClient,
} from './models';

interface DatadogRumConfig {
    env: DatadogEnvironment;
    service: string;
}

const DEFAULT_DATADOG_SESSION_SAMPLE_RATE = 100;
const DEFAULT_DATADOG_SESSION_REPLAY_SAMPLE_RATE = 20;
const DEFAULT_DATADOG_PRIVACY_LEVEL: DatadogPrivacyLevel = 'mask-user-input';
const DATADOG_PRIVACY_LEVELS = new Set<DatadogPrivacyLevel>([
    'allow',
    'mask',
    'mask-unless-allowlisted',
    'mask-user-input',
]);

const DATADOG_RUM_METADATA_BY_ENV = {
    staging: {
        env: 'staging',
        service: 'quipex-ui-staging',
    },
    prod: {
        env: 'prod',
        service: 'quipex-ui',
    },
} satisfies Record<DatadogEnvironment, DatadogRumConfig>;

@Injectable({
    providedIn: 'root',
})
export class DatadogService {
    //#region Dependency injection
    private readonly me = injectUser();
    private readonly org = injectOrg();
    private readonly destroyRef = inject(DestroyRef);
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly loadRum = inject(DATADOG_RUM_LOADER);
    //#endregion

    //#region State
    private rum?: DatadogRumClient;
    private initialization?: Promise<void>;
    private initialized = false;
    //#endregion

    //#region Public methods
    initialize(): Promise<void> {
        if (this.initialized) {
            return Promise.resolve();
        }

        if (this.initialization) {
            return this.initialization;
        }

        const config = this.getRumConfig(this.appConfig);
        if (!config) {
            return Promise.resolve();
        }

        const applicationId = this.appConfig.datadogRum?.applicationId?.trim();
        const clientToken = this.appConfig.datadogRum?.clientToken?.trim();
        if (!applicationId || !clientToken) {
            console.warn(
                '[DatadogService] Datadog RUM disabled: applicationId or clientToken not configured'
            );
            return Promise.resolve();
        }

        const sessionSampleRate = this.getSampleRate(
            this.appConfig.datadogRum?.sessionSampleRate,
            'sessionSampleRate',
            DEFAULT_DATADOG_SESSION_SAMPLE_RATE
        );
        const sessionReplaySampleRate = this.getSampleRate(
            this.appConfig.datadogRum?.sessionReplaySampleRate,
            'sessionReplaySampleRate',
            DEFAULT_DATADOG_SESSION_REPLAY_SAMPLE_RATE
        );
        const defaultPrivacyLevel = this.getPrivacyLevel(
            this.appConfig.datadogRum?.defaultPrivacyLevel
        );

        this.initialization = this.getRumClient()
            .then((rum) => {
                rum.init({
                    applicationId,
                    clientToken,
                    // `site` refers to the Datadog site parameter of your organization
                    // see https://docs.datadoghq.com/getting_started/site/
                    site: 'ap2.datadoghq.com',
                    service: config.service,
                    env: config.env,
                    version: this.appConfig.appVersion || undefined,
                    sessionSampleRate,
                    sessionReplaySampleRate,
                    trackResources: true,
                    trackLongTasks: true,
                    trackUserInteractions: true,
                    enablePrivacyForActionName: true,
                    defaultPrivacyLevel,
                });
                rum.setGlobalContextProperty(
                    'deployment_environment',
                    this.appConfig.env
                );
                this.initialized = true;
                this.setupUserTracking();
            })
            .catch((error: unknown) => {
                console.warn(
                    '[DatadogService] Failed to initialize Datadog RUM:',
                    error
                );
            })
            .finally(() => {
                this.initialization = undefined;
            });

        return this.initialization;
    }
    //#endregion

    //#region Private methods
    private setupUserTracking(): void {
        // Subscribe to user state and set Datadog user when available
        this.me.required$
            .pipe(
                filter((user) => !!user && !!user.id),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((user) => {
                this.rum?.setUser({
                    id: user.id,
                    name: user.displayName,
                    email: user.email,
                    organization: {
                        id: user.organisationId,
                        name: this.org()?.name,
                    },
                });
            });
    }

    private async getRumClient(): Promise<DatadogRumClient> {
        this.rum ??= await this.loadRum();
        return this.rum;
    }

    private getRumConfig(appConfig: AppConfig): DatadogRumConfig | null {
        if (appConfig.isProd) {
            return DATADOG_RUM_METADATA_BY_ENV.prod;
        }

        if (
            appConfig.env === 'qa' ||
            appConfig.env === 'uat' ||
            appConfig.isDemo
        ) {
            return DATADOG_RUM_METADATA_BY_ENV.staging;
        }

        return null;
    }

    private getSampleRate(
        value: string | undefined,
        label: string,
        fallback: number
    ): number {
        const trimmedValue = value?.trim();
        if (!trimmedValue) {
            return fallback;
        }

        const parsedValue = Number(trimmedValue);
        if (
            Number.isFinite(parsedValue) &&
            parsedValue >= 0 &&
            parsedValue <= 100
        ) {
            return parsedValue;
        }

        console.warn(
            `[DatadogService] Invalid Datadog RUM ${label}: ${trimmedValue}. Falling back to ${fallback}.`
        );
        return fallback;
    }

    private getPrivacyLevel(value: string | undefined): DatadogPrivacyLevel {
        const trimmedValue = value?.trim() as DatadogPrivacyLevel | undefined;
        if (!trimmedValue) {
            return DEFAULT_DATADOG_PRIVACY_LEVEL;
        }

        if (DATADOG_PRIVACY_LEVELS.has(trimmedValue)) {
            return trimmedValue;
        }

        console.warn(
            `[DatadogService] Invalid Datadog RUM defaultPrivacyLevel: ${trimmedValue}. Falling back to ${DEFAULT_DATADOG_PRIVACY_LEVEL}.`
        );
        return DEFAULT_DATADOG_PRIVACY_LEVEL;
    }
    //#endregion
}
